// Globals
import React, { useCallback } from 'react';
import { ValidateErrorEntity } from 'rc-field-form/lib/interface';
import { useTranslation } from 'react-i18next';

// Store
import { useAppDispatch, useAppState } from '@/store';
import { bannersSelector, createBanner, editBanner, getBannersList } from '../../feature';

// Components
import { IFormInstance, showErrorToast, showSuccessToast } from '@/components';
import { StyledPageWrapper } from './CreateBannerContainerStyles';
import { CreateBannerForm } from '../../components';
import { Skeleton } from '@/ui';

// Helpers
import { BannersFields, TBannerFormValues } from '../../helpers';
import CreateBannerDTO from '../../dtos/CreateBannerDTO';

type TCreateBannerContainer = {
  form: IFormInstance;
  onFormSubmit: () => void;
  isOpen: boolean;
};

const CreateBannerContainer = ({ form, onFormSubmit, isOpen }: TCreateBannerContainer) => {
  const { t } = useTranslation(['banners']);
  const dispatch = useAppDispatch();
  const { banner, isLoadingBanner, isLoading } = useAppState(bannersSelector);
  const isEdit = !!banner?.id;

  const handleSubmit = useCallback(
    async (values: TBannerFormValues) => {
      const bannerDto = new CreateBannerDTO(values);

      if (isEdit) {
        await dispatch(editBanner({ id: banner?.id, data: bannerDto })).unwrap();
      } else {
        await dispatch(createBanner(bannerDto)).unwrap();
      }

      await dispatch(getBannersList({ page: 1 })).unwrap();
      onFormSubmit();
      showSuccessToast({ message: t('data_saved') });
    },
    [onFormSubmit, isEdit],
  );

  const onFinishFailed = (error: ValidateErrorEntity) => {
    const missingLangs = error.errorFields.map((f) => f.name[0]);

    if (missingLangs.includes(BannersFields.LOCALE)) {
      showErrorToast({ message: t('create_banner_validation_locale_error') });
    }
  };

  if (isLoadingBanner) return <Skeleton />;

  return (
    <StyledPageWrapper>
      <CreateBannerForm
        onSubmit={handleSubmit}
        initialValues={banner}
        formInstance={form}
        isEdit={isEdit}
        isOpen={isOpen}
        isLoading={isLoading}
        onFinishFailed={onFinishFailed}
      />
    </StyledPageWrapper>
  );
};

export default CreateBannerContainer;
